// src/components/AccountHoverCard.js
//
// The account card of the desktop rail (mission 005): hovering the connected
// avatar opens a compact ledger-style table of what the Profile page holds —
// Balance (USD) with Add funds, My Passes, My Tickets, Settings (creators) —
// plus identity and Log out. Rendered inside components/ui/hover-card.js
// (mounted only while open, so its reads happen on open). The balance comes
// from useBalanceSummary: the existing session only (no signature prompt),
// cached 30 s, "—" when unknown — never $0.
//
// "Sleek table" (docs/ui-rules.md): mono tabular-nums values, hairline rows,
// right-aligned numbers. Lime only for the online dot.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, LogOut } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { formatUsd } from '../lib/usd';
import { useBalanceSummary } from '../hooks/useBalanceSummary';
import { useUserTickets } from '../hooks/useUserTickets';

function usePassCount(walletAddress) {
  const [n, setN] = useState(null);
  useEffect(() => {
    if (!walletAddress) return undefined;
    let live = true;
    supabase.from('card_holders').select('token_id', { count: 'exact', head: true })
      .eq('owner_wallet', walletAddress.toLowerCase())
      .then(({ count, error }) => { if (live) setN(error ? null : count ?? 0); });
    return () => { live = false; };
  }, [walletAddress]);
  return n;
}

function Row({ to, label, value, onNavigate }) {
  return (
    <Link to={to} onClick={onNavigate}
      className="flex min-h-[44px] items-center justify-between gap-3 border-b border-white/[0.05] px-4 text-[14px] text-zinc-300 outline-none hover:bg-white/[0.03] hover:text-white focus-visible:bg-white/[0.05] focus-visible:text-white">
      <span>{label}</span>
      <span className="flex items-center gap-1 font-mono text-[13px] tabular-nums text-zinc-400">
        {value}<ChevronRight size={14} className="text-zinc-600" />
      </span>
    </Link>
  );
}

export default function AccountCardContent({ identity, walletAddress, email = null, isCreator, onNavigate, onLogout }) {
  const { summary } = useBalanceSummary();
  const passes = usePassCount(walletAddress);
  const { tickets, loading: ticketsLoading } = useUserTickets(walletAddress);
  // an email session shows its email where a wallet session shows the short address (mission 007)
  const short = email || (walletAddress ? `${walletAddress.slice(0, 6)}…${walletAddress.slice(-4)}` : '');
  const pending = (summary?.held_cents ?? 0) + (summary?.reserved_cents ?? 0);

  return (
    <div className="overflow-hidden">
      <Link to="/profile" onClick={onNavigate}
        className="flex items-center gap-3 border-b border-white/[0.05] px-4 py-3 outline-none hover:bg-white/[0.03] focus-visible:bg-white/[0.05]">
        <div className="relative">
          <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-zinc-800 text-xs font-medium">
            {identity?.avatarUrl ? <img src={identity.avatarUrl} alt="" className="h-full w-full object-cover" /> : identity?.name?.slice(0, 2).toUpperCase()}
          </div>
          <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[#141414] bg-[#A3E635]" />
        </div>
        <div className="min-w-0">
          <div className="truncate text-[14px] font-semibold text-white">@{identity?.name}</div>
          <div className={`truncate text-[11px] text-zinc-500 ${email ? '' : 'font-mono'}`}>{short}</div>
        </div>
      </Link>

      <div className="border-b border-white/[0.05] px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="text-[14px] text-zinc-300">Balance</span>
          <span className="font-mono text-[15px] font-semibold tabular-nums text-white">{summary ? formatUsd(summary.available_cents) : '—'}</span>
        </div>
        {summary && pending > 0 && (
          <div className="mt-0.5 text-right font-mono text-[11px] tabular-nums text-zinc-500">
            {summary.held_cents > 0 && `${formatUsd(summary.held_cents)} on hold`}
            {summary.held_cents > 0 && summary.reserved_cents > 0 && ' · '}
            {summary.reserved_cents > 0 && `${formatUsd(summary.reserved_cents)} reserved`}
          </div>
        )}
        <Link to="/profile?tab=funds" onClick={onNavigate}
          className="mt-2.5 flex h-9 w-full items-center justify-center rounded-[10px] bg-white text-[13px] font-semibold text-black outline-none hover:bg-zinc-200 focus-visible:ring-2 focus-visible:ring-white/40 active:scale-[0.98]">
          Add funds
        </Link>
      </div>

      <Row to="/profile?tab=positions" label="My Passes" value={passes ?? '—'} onNavigate={onNavigate} />
      <Row to="/profile?tab=tickets" label="My Tickets" value={ticketsLoading && !tickets.length ? '—' : tickets.length} onNavigate={onNavigate} />
      {isCreator && <Row to="/profile?tab=settings" label="Settings" value="" onNavigate={onNavigate} />}

      <button type="button" onClick={onLogout}
        className="flex min-h-[44px] w-full items-center gap-2 px-4 text-[14px] text-zinc-500 outline-none hover:text-red-400 focus-visible:text-red-400">
        <LogOut size={15} strokeWidth={1.5} /> Log out
      </button>
    </div>
  );
}
